import React, { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DF_OT0050Context } from "../../context";
import { Row, Col, Button, Input, Label, FormGroup } from "reactstrap";
import BizinBackend from "../../../../helpers/bizin_helper";
import useGetCsrfToken from "../../../../Hooks/useGetCsrfToken";

const inputClassName = "rounded font-size-14 border-color-cinza2";

function GridBody({ rowData }) {
  const { setisLoading, setTriggerOnFilter } =
    useContext(DF_OT0050Context) || {};
  const navigate = useNavigate();

  const { access_token, id_usuario } = useSelector((state) => ({
    access_token: state.login.user.access_token,
    id_usuario: state.login.user.id_user,
  }));

  const csrfToken = useGetCsrfToken();

  const formatHora = (value) => value?.replace(":", "") || null;

  const parseHora = (value) => {
    if (!value) return "";
    const clean = String(value).replace(":", "");
    if (clean.length === 4) return `${clean.slice(0, 2)}:${clean.slice(2)}`;
    return value;
  };

  const [formData, setFormData] = useState({
    idLocalEntrega: rowData?.ID ?? rowData?.ID_Local_Entrega ?? null,
    cnpj: rowData?.CNPJ_Local_Entrega ?? "",
    descricao: rowData?.Descricao_Local_Entrega ?? "",
    horarioInicio: parseHora(rowData?.Horario_Inicio ?? ""),
    horarioTermino: parseHora(rowData?.Horario_Termino ?? ""),
    qtdVolume: rowData?.Limite_Diario_Max_Volumes ?? "",
    valorMercadoria: rowData?.Limite_Diario_Max_Valor ?? "",
    pesoBruto: rowData?.Limite_Diario_Max_Peso ?? "",
    qtdVeiculos: rowData?.Limite_Diario_Max_Veiculos ?? "",
    limiteHorario: parseHora(
      rowData?.Limite_Max_Horario_Prog_Agendamento ?? "",
    ),
    diasAnteriores: rowData?.Limite_Min_Dias_Prog_Agendamento ?? "",
    diasPosteriores: rowData?.Limite_Max_Dias_Prog_Agendamento ?? "",
    limiteAgendamentos: rowData?.Limite_Agendamentos_Diarios_Parceiro ?? "",
    dataEncerramento: rowData?.Data_Encerramento
      ? rowData.Data_Encerramento.split("T")[0]
      : "",
    observacao: rowData?.Observacao ?? "",
    mostrarHorario: rowData?.Mostra_Horario === "S",
  });

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    if (!formData.cnpj || !formData.descricao) return;

    try {
      if (setisLoading) setisLoading(true);

      const logResponse = await BizinBackend.executeProcedure(
        {
          ID_Usuario: id_usuario || null,
          ID_Objeto: formData.idLocalEntrega
            ? parseInt(formData.idLocalEntrega, 10)
            : null,
          Objeto: "LOCAL_ENTREGA",
          Acao: "UPD",
          Mensagem: `Alteração do Local de Entrega ID: ${formData.idLocalEntrega} pelo usuário <#USUARIO#>`,
        },
        "FRONT_Logs_Inserir_Cabecalho",
        csrfToken,
        access_token,
      );

      const idLog = Array.isArray(logResponse)
        ? parseInt(logResponse[0]?.ID_Log, 10) || null
        : null;

      if (!idLog) {
        console.error("Falha ao obter ID_Log — abortando salvamento.");
        return;
      }

      const payload = {
        ID_Local_Entrega: formData.idLocalEntrega
          ? parseInt(formData.idLocalEntrega, 10)
          : null,
        CNPJ_Local_Entrega:
          String(formData.cnpj).replace(/\D/g, "").slice(0, 14) || null,
        Descricao_Local_Entrega: formData.descricao || null,
        Horario_Inicio: formatHora(formData.horarioInicio) || null,
        Horario_Termino: formatHora(formData.horarioTermino) || null,
        Limite_Diario_Max_Volumes: formData.qtdVolume
          ? parseInt(formData.qtdVolume, 10)
          : null,
        Limite_Diario_Max_Valor: formData.valorMercadoria
          ? parseFloat(formData.valorMercadoria)
          : null,
        Limite_Diario_Max_Peso: formData.pesoBruto
          ? parseFloat(formData.pesoBruto)
          : null,
        Limite_Diario_Max_Veiculos: formData.qtdVeiculos
          ? parseInt(formData.qtdVeiculos, 10)
          : null,
        Limite_Max_Horario_Prog_Agendamento:
          formatHora(formData.limiteHorario) || null,
        Limite_Min_Dias_Prog_Agendamento: formData.diasAnteriores
          ? parseInt(formData.diasAnteriores, 10)
          : null,
        Limite_Max_Dias_Prog_Agendamento: formData.diasPosteriores
          ? parseInt(formData.diasPosteriores, 10)
          : null,
        Limite_Agendamentos_Diarios_Parceiro: formData.limiteAgendamentos
          ? parseInt(formData.limiteAgendamentos, 10)
          : null,
        Data_Encerramento: formData.dataEncerramento
          ? `${formData.dataEncerramento}T00:00:00`
          : null,
        ID_Calendario: null,
        Observacao: formData.observacao || null,
        Limite_Diario_Max_Cubagem: null,
        ID_Log: idLog,
        ID_Tipo_Local_Entrega: null,
        Mostra_Horario: formData.mostrarHorario ? "S" : "N",
      };

      await BizinBackend.executeProcedure(
        payload,
        "SRM_Agendamento_Altera_Locais_Entrega",
        csrfToken,
        access_token,
      );

      navigate("/DF_OT0050");
    } catch (error) {
      console.error("Erro ao alterar local de entrega:", error);
    } finally {
      if (setisLoading) setisLoading(false);
    }
  };

  return (
    <div style={{ padding: "0 10px" }}>
      <p className="text-muted font-size-14 mb-3 font-weight-bold">
        Informações do Local de Entrega
      </p>

      <Row className="gx-2 mb-0">
        <Col lg={3}>
          <FormGroup className="mb-2">
            <Label for="cnpj">CNPJ</Label>
            <Input
              type="text"
              id="cnpj"
              value={formData.cnpj}
              onChange={handleInputChange}
              className={inputClassName}
            />
          </FormGroup>
        </Col>
        <Col lg={3}>
          <FormGroup className="mb-2">
            <Label for="descricao">Descrição</Label>
            <Input
              type="text"
              id="descricao"
              value={formData.descricao}
              onChange={handleInputChange}
              className={inputClassName}
            />
          </FormGroup>
        </Col>
        <Col lg={3}>
          <FormGroup className="mb-2">
            <Label for="horarioInicio">Horário início</Label>
            <Input
              type="time"
              id="horarioInicio"
              value={formData.horarioInicio}
              onChange={handleInputChange}
              className={inputClassName}
            />
          </FormGroup>
        </Col>
        <Col lg={3}>
          <FormGroup className="mb-2">
            <Label for="horarioTermino">Horário término</Label>
            <Input
              type="time"
              id="horarioTermino"
              value={formData.horarioTermino}
              onChange={handleInputChange}
              className={inputClassName}
            />
          </FormGroup>
        </Col>
      </Row>

      <Row className="gx-2 mb-0">
        <Col lg={3}>
          <FormGroup className="mb-2">
            <Label for="qtdVolume">Quantidade volume</Label>
            <Input
              type="number"
              id="qtdVolume"
              value={formData.qtdVolume}
              onChange={handleInputChange}
              className={inputClassName}
            />
          </FormGroup>
        </Col>
        <Col lg={3}>
          <FormGroup className="mb-2">
            <Label for="valorMercadoria">Valor da mercadoria</Label>
            <Input
              type="number"
              id="valorMercadoria"
              value={formData.valorMercadoria}
              onChange={handleInputChange}
              className={inputClassName}
            />
          </FormGroup>
        </Col>
        <Col lg={3}>
          <FormGroup className="mb-2">
            <Label for="pesoBruto">Peso bruto (Kg)</Label>
            <Input
              type="number"
              id="pesoBruto"
              value={formData.pesoBruto}
              onChange={handleInputChange}
              className={inputClassName}
            />
          </FormGroup>
        </Col>
        <Col lg={3}>
          <FormGroup className="mb-2">
            <Label for="qtdVeiculos">Qtd. veículos</Label>
            <Input
              type="number"
              id="qtdVeiculos"
              value={formData.qtdVeiculos}
              onChange={handleInputChange}
              className={inputClassName}
            />
          </FormGroup>
        </Col>
      </Row>

      <Row className="gx-2 mb-0">
        <Col lg={3}>
          <FormGroup className="mb-2">
            <Label for="limiteHorario">Limite de horário</Label>
            <Input
              type="time"
              id="limiteHorario"
              value={formData.limiteHorario}
              onChange={handleInputChange}
              className={inputClassName}
            />
          </FormGroup>
        </Col>
        <Col lg={3}>
          <FormGroup className="mb-2">
            <Label for="diasAnteriores">Dias Anteriores dt. Entrega</Label>
            <Input
              type="number"
              id="diasAnteriores"
              value={formData.diasAnteriores}
              onChange={handleInputChange}
              className={inputClassName}
            />
          </FormGroup>
        </Col>
        <Col lg={3}>
          <FormGroup className="mb-2">
            <Label for="diasPosteriores">Dias Posteriores dt. Entrega</Label>
            <Input
              type="number"
              id="diasPosteriores"
              value={formData.diasPosteriores}
              onChange={handleInputChange}
              className={inputClassName}
            />
          </FormGroup>
        </Col>
        <Col lg={3}>
          <FormGroup className="mb-2">
            <Label for="limiteAgendamentos">
              Limite de Agendamentos Diários por Parceiro
            </Label>
            <Input
              type="number"
              id="limiteAgendamentos"
              value={formData.limiteAgendamentos}
              onChange={handleInputChange}
              className={inputClassName}
            />
          </FormGroup>
        </Col>
      </Row>

      <Row className="gx-2 mb-0">
        <Col lg={3}>
          <FormGroup className="mb-2">
            <Label for="dataEncerramento">Data de Encerramento</Label>
            <Input
              type="date"
              id="dataEncerramento"
              value={formData.dataEncerramento}
              onChange={handleInputChange}
              className={inputClassName}
            />
          </FormGroup>
        </Col>
        <Col lg={6}>
          <FormGroup className="mb-2">
            <Label for="observacao">Observação</Label>
            <Input
              type="textarea"
              id="observacao"
              value={formData.observacao}
              onChange={handleInputChange}
              rows="1"
              className={inputClassName}
              style={{ resize: "none" }}
            />
          </FormGroup>
        </Col>
        <Col lg={3}>
          <FormGroup check className="mt-4 pt-1">
            <Label check className="text-muted font-size-13">
              <Input
                type="checkbox"
                id="mostrarHorario"
                checked={formData.mostrarHorario}
                onChange={handleInputChange}
              />{" "}
              Mostrar horário de agendamento?
            </Label>
          </FormGroup>
        </Col>
      </Row>

      <Row className="mt-2">
        <Col lg={12} className="d-flex justify-content-end">
          <Button
            className="btn-acao-gravar bg-primary-dark border-0 text-white font-w-600 rounded"
            onClick={handleSave}
          >
            Gravar
          </Button>
        </Col>
      </Row>
    </div>
  );
}

export default GridBody;
